import React, { useState } from 'react';
import { Loader2, Sparkles, AlertTriangle } from 'lucide-react';
import Navigation from './components/Navigation';
import AudioToVideo from './views/AudioToVideo';
import VideoToAudio from './views/VideoToAudio';
import MergeAudioVideo from './views/MergeAudioVideo';
import NoiseRemover from './views/NoiseRemover';
import VoiceEnhancer from './views/VoiceEnhancer';
import EmbedComment from './views/EmbedComment'; // NOUVO: Enpòte View a

const modalMessages = {
  'audio-to-video': { title: 'Rendering Video', desc: 'Your video is being prepared frame by frame for maximum quality.' },
  'video-to-audio': { title: 'Extracting Audio', desc: 'We are pulling the high-quality sound from your video file.' },
  'merge-audio-video': { title: 'Merging Files', desc: 'Combining your audio and video streams into a single file.' },
  'noise-remover': { title: 'Cleaning Audio', desc: 'AI is analyzing frequencies to isolate your voice and remove noise.' },
  'voice-enhancer': { title: 'Enhancing Voice', desc: 'Applying Pro Studio EQ and Compression to your recording.' },
  'embed-comment': { title: 'Embedding Comment', desc: 'Creating a high-quality vertical video with your social media comment overlay.' } // NOUVO
};

const App = () => {
  const [activeTool, setActiveTool] = useState('audio-to-video');
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const currentMsg = modalMessages[activeTool] || { title: 'Processing', desc: 'Please wait while we handle your request.' };

  return (
    <div className={`min-h-screen bg-slate-950 text-slate-50 font-sans antialiased ${isExporting ? 'overflow-hidden' : ''}`}>
      <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet" />

      {isExporting && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4 text-center export-modal"
          style={{
            zIndex: 10000,
            backgroundColor: '#020617',
            opacity: 0.99,
            display: 'flex'
          }}
        >
          {/* Glow Effects nan background */}
          <div className="absolute inset-0 opacity-10 pointer-events-none overflow-hidden">
            <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-white rounded-full blur-[150px]"></div>
            <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-slate-500 rounded-full blur-[150px]"></div>
          </div>

          <div
            className="relative max-w-lg w-full bg-slate-900 border-2 border-slate-800 p-12 shadow-2xl animate-in zoom-in duration-300"
            style={{ borderRadius: '50px', overflow: 'hidden' }}
          >
            {/* LOADER & ICONS */}
            <div className="relative inline-block mb-10">
              <Loader2 style={{ color: '#ffffff' }} className="animate-spin" size={70} />
              <Sparkles className="absolute -top-3 -right-3 animate-pulse" style={{ color: '#ffffff' }} size={30} />
            </div>

            {/* TITLE */}
            <h2 className="text-4xl font-black mb-6 uppercase tracking-tighter">
              {exportProgress > 0 ? 'Converting' : currentMsg.title}
            </h2>

            {/* PROGRESS BAR */}
            {exportProgress > 0 && (
              <div className="mb-10 space-y-4 text-left">
                <div className="flex justify-between text-xs font-black uppercase tracking-widest px-1">
                  <span>Encoding Progress</span>
                  <span>{exportProgress}%</span>
                </div>
                <div className="w-full bg-slate-950 h-4 rounded-full overflow-hidden border border-slate-800 shadow-inner">
                  <div
                    className="h-full transition-all duration-300"
                    style={{ width: `${exportProgress}%`, backgroundColor: '#ffffff' }}
                  ></div>
                </div>
              </div>
            )}

            {/* DESCRIPTION */}
            <div className="space-y-6 mb-10">
              <p className="text-2xl font-black leading-tight">
                Please do not close this window.
              </p>
              <p className="text-sm font-medium leading-relaxed max-w-xs mx-auto">
                {currentMsg.desc}
              </p>
            </div>

            {/* STATUS BAR */}
            <div className="py-6 px-8 bg-slate-950 rounded-[30px] border border-slate-800 flex items-center gap-4 justify-center">
              <AlertTriangle style={{ color: '#ffffff' }} size={24} />
              <span className="text-[11px] font-black uppercase tracking-[0.2em] text-left leading-tight">
                Status: High-Speed Engine Running.<br />
                <span>Action: Rendering Assets...</span>
              </span>
            </div>
          </div>
        </div>
      )}

      <Navigation activeTool={activeTool} setActiveTool={setActiveTool} />

      <main className="max-w-4xl mx-auto py-12 px-6">
        {activeTool === 'audio-to-video' && (
          <AudioToVideo setIsExporting={setIsExporting} setExportProgress={setExportProgress} />
        )}
        {activeTool === 'video-to-audio' && (
          <VideoToAudio setIsExporting={setIsExporting} setExportProgress={setExportProgress} />
        )}
        {activeTool === 'merge-audio-video' && (
          <MergeAudioVideo setIsExporting={setIsExporting} setExportProgress={setExportProgress} />
        )}
        {activeTool === 'noise-remover' && (
          <NoiseRemover setIsExporting={setIsExporting} setExportProgress={setExportProgress} />
        )}
        {activeTool === 'voice-enhancer' && (
          <VoiceEnhancer setIsExporting={setIsExporting} setExportProgress={setExportProgress} />
        )}
        {/* NOUVO: Kondisyon pou Embed Comment */}
        {activeTool === 'embed-comment' && (
          <EmbedComment setIsExporting={setIsExporting} setExportProgress={setExportProgress} />
        )}
      </main>

      <style>{`
        .shadow-neon { box-shadow: 0 0 20px rgba(245, 158, 11, 0.4); }
        .fade-in { animation: fadeIn 0.6s ease-out forwards; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

        .export-modal,
        .export-modal * {
          color: #ffffff !important;
        }
      `}</style>
    </div>
  );
};

export default App;

// import React, { useState } from 'react';
// import { Loader2, Sparkles, AlertTriangle } from 'lucide-react';
// import Navigation from './components/Navigation';
// import AudioToVideo from './views/AudioToVideo';
// import VideoToAudio from './views/VideoToAudio';
// import MergeAudioVideo from './views/MergeAudioVideo';
// import NoiseRemover from './views/NoiseRemover';
// import VoiceEnhancer from './views/VoiceEnhancer';


// const modalMessages = {
//   'audio-to-video': { title: 'Rendering Video', desc: 'Your video is being prepared frame by frame for maximum quality.' },
//   'video-to-audio': { title: 'Extracting Audio', desc: 'We are pulling the high-quality sound from your video file.' },
//   'merge-audio-video': { title: 'Merging Files', desc: 'Combining your audio and video streams into a single file.' },
//   'noise-remover': { title: 'Cleaning Audio', desc: 'AI is analyzing frequencies to isolate your voice and remove noise.' },
//   'voice-enhancer': { title: 'Enhancing Voice', desc: 'Applying Pro Studio EQ and Compression to your recording.' } // NOUVO
// };


// const App = () => {
//   const [activeTool, setActiveTool] = useState('audio-to-video');
//   const [isExporting, setIsExporting] = useState(false);
//   const [exportProgress, setExportProgress] = useState(0);
//   const currentMsg = modalMessages[activeTool] || { title: 'Processing', desc: 'Please wait while we handle your request.' };

//   return (
//     <div className={`min-h-screen bg-slate-950 text-slate-50 font-sans antialiased ${isExporting ? 'overflow-hidden' : ''}`}>
//       <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet" />

//       {isExporting && (
//         <div
//           className="fixed inset-0 flex items-center justify-center p-4 text-center export-modal"
//           style={{
//             zIndex: 10000,
//             backgroundColor: '#020617',
//             opacity: 0.99,
//             display: 'flex'
//           }}
//         >
//           {/* Glow Effects nan background */}
//           <div className="absolute inset-0 opacity-10 pointer-events-none overflow-hidden">
//             <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-white rounded-full blur-[150px]"></div>
//             <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-slate-500 rounded-full blur-[150px]"></div>
//           </div>

//           <div
//             className="relative max-w-lg w-full bg-slate-900 border-2 border-slate-800 p-12 shadow-2xl animate-in zoom-in duration-300"
//             style={{ borderRadius: '50px', overflow: 'hidden' }}
//           >

//             {/* LOADER & ICONS */}
//             <div className="relative inline-block mb-10">
//               <Loader2 style={{ color: '#ffffff' }} className="animate-spin" size={70} />
//               <Sparkles className="absolute -top-3 -right-3 animate-pulse" style={{ color: '#ffffff' }} size={30} />
//             </div>

//             {/* TITLE */}
//             <h2 className="text-4xl font-black mb-6 uppercase tracking-tighter">
//               {exportProgress > 0 ? 'Converting' : currentMsg.title}
//             </h2>

//             {/* PROGRESS BAR */}
//             {exportProgress > 0 && (
//               <div className="mb-10 space-y-4 text-left">
//                 <div className="flex justify-between text-xs font-black uppercase tracking-widest px-1">
//                   <span>Encoding Progress</span>
//                   <span>{exportProgress}%</span>
//                 </div>
//                 <div className="w-full bg-slate-950 h-4 rounded-full overflow-hidden border border-slate-800 shadow-inner">
//                   <div
//                     className="h-full transition-all duration-300"
//                     style={{ width: `${exportProgress}%`, backgroundColor: '#ffffff' }}
//                   ></div>
//                 </div>
//               </div>
//             )}

//             {/* DESCRIPTION */}
//             <div className="space-y-6 mb-10">
//               <p className="text-2xl font-black leading-tight">
//                 Please do not close this window.
//               </p>
//               <p className="text-sm font-medium leading-relaxed max-w-xs mx-auto">
//                 {currentMsg.desc}
//               </p>
//             </div>

//             {/* STATUS BAR */}
//             <div className="py-6 px-8 bg-slate-950 rounded-[30px] border border-slate-800 flex items-center gap-4 justify-center">
//               <AlertTriangle style={{ color: '#ffffff' }} size={24} />
//               <span className="text-[11px] font-black uppercase tracking-[0.2em] text-left leading-tight">
//                 Status: High-Speed Engine Running.<br />
//                 <span>Action: Rendering Assets...</span>
//               </span>
//             </div>
//           </div>
//         </div>
//       )}

//       <Navigation activeTool={activeTool} setActiveTool={setActiveTool} />

//       <main className="max-w-4xl mx-auto py-12 px-6">
//         {activeTool === 'audio-to-video' && (
//           <AudioToVideo setIsExporting={setIsExporting} setExportProgress={setExportProgress} />
//         )}
//         {activeTool === 'video-to-audio' && (
//           <VideoToAudio setIsExporting={setIsExporting} setExportProgress={setExportProgress} />
//         )}
//         {activeTool === 'merge-audio-video' && (
//           <MergeAudioVideo setIsExporting={setIsExporting} setExportProgress={setExportProgress} />
//         )}
//         {activeTool === 'noise-remover' && (
//           <NoiseRemover setIsExporting={setIsExporting} setExportProgress={setExportProgress} />
//         )}
//         {activeTool === 'voice-enhancer' && (
//           <VoiceEnhancer setIsExporting={setIsExporting} setExportProgress={setExportProgress} />
//         )}
//       </main>

//       <style>{`
//         .shadow-neon { box-shadow: 0 0 20px rgba(245, 158, 11, 0.4); }
//         .fade-in { animation: fadeIn 0.6s ease-out forwards; }
//         @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

//         /* Force all text inside the export modal to be white */
//         .export-modal,
//         .export-modal * {
//           color: #ffffff !important;
//         }
//       `}</style>
//     </div>
//   );
// };

// export default App;